import {CheckIcon, CommentIcon, DotFillIcon, FileDiffIcon, MoonIcon, ShieldIcon, SyncIcon} from '@primer/octicons-react'
import type {Icon} from '@primer/octicons-react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {ownerPath, teamPath} from '@github-ui/paths'
import type {
  ReviewRequestsData,
  CompletedReview,
  PendingReviewRequest,
  Reviewer,
} from '../../page-data/payloads/review-requests'
import {SidebarSection} from './SidebarSection'
import styles from './Reviewers.module.css'
import {pullRequestsOverviewRoute} from '../../routes/overview-route'
import {pullRequestsChangesWithRangeRoute} from '../../routes/files-changed-routes'
import {useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {IconButton, Link as PrimerLink} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense} from 'react'
import {ReviewersSkeleton} from './ReviewersSkeleton'
import {Link} from '@github-ui/react-core/link'

interface ReviewersProps {
  reviewers: ReviewRequestsData
}

export function Reviewers() {
  return (
    <Suspense fallback={<ReviewersSkeleton />}>
      <ReviewersContent />
    </Suspense>
  )
}

function ReviewersContent() {
  const {data: reviewers} = useSuspenseRouteQuery(pullRequestsOverviewRoute, 'review_requests')

  return <ReviewersComponent reviewers={reviewers} />
}

/** Reviewers section in the sidebar */
export function ReviewersComponent({reviewers}: ReviewersProps) {
  const completedReviews = reviewers.completedReviews ?? []
  const pendingRequests = reviewers.pendingReviewRequests ?? []
  const suggestedReviewers = reviewers.suggestedReviewers ?? []

  if (completedReviews.length === 0 && pendingRequests.length === 0 && suggestedReviewers.length === 0) {
    return (
      <SidebarSection title="Reviewers">
        <span className={styles.emptyState}>No reviewers</span>
      </SidebarSection>
    )
  }

  return (
    <SidebarSection title="Reviewers">
      <ul className={styles.reviewerList}>
        {completedReviews.map(review => (
          <CompletedReviewRow key={review.reviewer.id} review={review} />
        ))}
        {pendingRequests.map(request => (
          <PendingReviewRow key={request.reviewer.id} request={request} />
        ))}
      </ul>
    </SidebarSection>
  )
}

function reviewerHref(reviewer: Reviewer): string {
  if (reviewer.reviewerType === 'Team' && reviewer.organizationLogin) {
    return teamPath({owner: reviewer.organizationLogin, team: reviewer.displayLogin})
  }
  return ownerPath({owner: reviewer.displayLogin})
}

function ReviewerIdentity({reviewer}: {reviewer: Reviewer}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const isTeam = reviewer.reviewerType === 'Team'
  const hovercardAttrs = isTeam
    ? getHovercardAttributesForType('team', {
        owner: reviewer.organizationLogin ?? '',
        team: reviewer.displayLogin,
      })
    : getHovercardAttributesForType('user', {login: reviewer.displayLogin})

  return (
    <PrimerLink href={reviewerHref(reviewer)} muted className={styles.reviewerIdentity} {...hovercardAttrs}>
      <GitHubAvatar src={reviewer.avatarUrl} size={20} square={isTeam} />
      <span className={styles.reviewerName}>{reviewer.displayLogin}</span>
    </PrimerLink>
  )
}

function reviewStateIcon(state: string): {icon: Icon; className: string} {
  switch (state.toUpperCase()) {
    case 'APPROVED':
      return {icon: CheckIcon, className: 'color-fg-success'}
    case 'CHANGES_REQUESTED':
      return {icon: FileDiffIcon, className: 'color-fg-danger'}
    case 'COMMENTED':
      return {icon: CommentIcon, className: 'color-fg-muted'}
    case 'DISMISSED':
      return {icon: ShieldIcon, className: 'color-fg-muted'}
    default:
      return {icon: DotFillIcon, className: 'color-fg-attention'}
  }
}

function reviewStateTooltip(state: string, name: string): string {
  switch (state.toUpperCase()) {
    case 'APPROVED':
      return `${name} approved these changes`
    case 'CHANGES_REQUESTED':
      return `${name} requested changes`
    case 'COMMENTED':
      return `${name} left review comments`
    case 'DISMISSED':
      return `${name}'s review was dismissed`
    default:
      return `Awaiting requested review from ${name}`
  }
}

function CompletedReviewRow({review}: {review: CompletedReview}) {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsOverviewRoute)
  const name = review.reviewer.displayLogin
  const {icon: stateIcon, className: stateClass} = reviewStateIcon(review.state)
  const changesWithRangePath = pullRequestsChangesWithRangeRoute.generatePath({
    owner,
    repo,
    pr_number,
    range: `BASE..${review.headSha}`,
  })

  return (
    <li className={styles.reviewerRow}>
      <ReviewerIdentity reviewer={review.reviewer} />
      <span className={styles.indicators}>
        {review.canReRequest && (
          <IconButton
            icon={SyncIcon}
            aria-label="Re-request review"
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
          />
        )}
        <IconButton
          as={Link}
          to={changesWithRangePath}
          icon={stateIcon}
          aria-label={reviewStateTooltip(review.state, name)}
          variant="invisible"
          size="small"
          tooltipDirection="nw"
          className={clsx(styles.statusIcon, stateClass)}
        />
      </span>
    </li>
  )
}

function deferredTooltip(deferredType: 'codeowner' | 'copilot', name: string): string {
  if (deferredType === 'copilot') {
    return `${name} will automatically review once the pull request is marked ready for review`
  }
  return `${name} will be requested when the pull request is marked ready for review`
}

function codeOwnerTooltip(request: PendingReviewRequest): string {
  const name = request.reviewer.displayLogin
  if (request.isCodeOwnerFromDelegation && request.delegatedFromTeamName) {
    return `${name} is a code owner automatically assigned from ${request.delegatedFromTeamName}`
  }
  return `${name} is a code owner`
}

function PendingReviewRow({request}: {request: PendingReviewRequest}) {
  const name = request.reviewer.displayLogin
  return (
    <li className={styles.reviewerRow}>
      <ReviewerIdentity reviewer={request.reviewer} />
      <span className={styles.indicators}>
        {request.deferredType !== null && (
          <IconButton
            icon={MoonIcon}
            aria-label={deferredTooltip(request.deferredType, name)}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
          />
        )}
        {(request.isCodeOwner || request.isCodeOwnerFromDelegation) && (
          <IconButton
            icon={ShieldIcon}
            aria-label={codeOwnerTooltip(request)}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
          />
        )}
        <IconButton
          icon={DotFillIcon}
          aria-label={`Awaiting requested review from ${name}`}
          variant="invisible"
          size="small"
          tooltipDirection="nw"
          className={clsx(styles.statusIcon, 'color-fg-attention')}
        />
      </span>
    </li>
  )
}
